import { hotkeys } from 'react-keyboard-shortcuts';
import { v4 as uuidv4 } from 'uuid';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import ActionText from '../core/ActionText';
import Errored from '../core/Errored';
import Loading from '../core/Loading';
import PagingFilter from '../core/PagingFilter';
import ProjectLanguageSelector from '../core/ProjectLanguageSelector';
import { globalStyles } from '../core/style/gstheme';
import { makeCancelable, sortResourcesWithFallback } from '../core/utils/utils';
import GS from '../grafsync/grafsync';
import ResourceData from './ResourceData';
import ResourceListStructure from './ResourceListStructure';
import CreateNodeDialogContainer from '../application-core/CreateNodeDialogContainer';
import CreateDataResourceDialog from './CreateDataResourceDialog';
import ComponentHint from '../core/ComponentHint';
import DatasetBatchOperations from './DatasetBatchOperations';

const PAGE_SIZE = 200;
const styles = Object.assign({}, globalStyles);

class ResourceView extends Component {

  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      initialSearch: true,
      resources: undefined,
      updateAt: undefined,
      text: "",
      searchID: null,
      noneFound: false,
      noResults: true,
      isErrored: false,
      viewLang: props.viewLang
    };
    this.infoRef = React.createRef();
    this.controlRef = React.createRef();
    this.cancellable = null;
  }

  hot_keys = {
    'alt+i': {
      priority: 1,
      handler: () => {
        if (this.infoRef && this.infoRef.current) {
          this.infoRef.current.focus();
        }
      }
    },
    'alt+c': {
      priority: 1,
      handler: () => {
        if (this.controlRef && this.controlRef.current) {
          this.controlRef.current.focus();
        }
      }
    }
  };

  componentDidMount() {
    this.handlePageChange(true);
  }

  static getDerivedStateFromProps(props, state) {
    if (props.viewLang !== state.viewLang) {
      return {
        isLoading: false,
        initialSearch: state.text === "",
        viewLang: props.viewLang,
        resources: undefined,
        noneFound: false,
        updateAt: undefined,
        noResults: true
      };
    }
    return null;
  }

  componentDidUpdate() {
    if (!this.state.isLoading && this.state.resources === undefined && this.state.noResults === true) {
      this.handlePageChange(this.state.initialSearch);
    }
  }

  handleFilterChange = (text) => {
    this.setState({ text, initialSearch: text === "" }, () => this.handlePageChange(text === ""));
  };

  handleChange = name => event => {
    this.setState({ [name]: event.target.checked }, () => this.handlePageChange(this.state.text === ""));
  };

  handlePageChange = async (initialSearch = false) => {
    if (!initialSearch && this.state.text.length < 1) return;
    this.setState(
      { isLoading: true },
      () => {
        let search = [
          {
            predicate: "https://grafsync.graphifi.com/schema/gs/inDataset",
            ...(this.props.datasetIRI) ?
              {
                value: this.props.datasetIRI
              }
              :
              {
                invert: true
              }
          },
          {
            predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
            value: "https://grafsync.graphifi.com/schema/gs/Resource"
          }
        ];
        if (this.state.text !== "") {
          search.push({
            predicate: ["http://www.w3.org/2000/01/rdf-schema#label"],
            value: this.state.text,
            isExactMatch: false
          });
        }
        const searchID = uuidv4();
        this.setState({ searchID }, () => {
          this.doSearch({
            search,
            noCount: true,
            allowMissingResults: true,
            retainOrder: initialSearch,
            ...(initialSearch) && { fast: true },
            graph: [this.props.graph],
            exclude: this.props.deletedResources,
            predicates: ["http://www.w3.org/2000/01/rdf-schema#label"],
            page: 1,
            language: this.props.viewLang,
            pageSize: PAGE_SIZE,
            sort: ["http://www.w3.org/2000/01/rdf-schema#label"]
          }, searchID)
            .promise
            .then(data => {
              // Ignore 'old' 
              if (this.cancellable && this.cancellable.isCancelled()) return;
              if (data.uuid && data.uuid !== this.state.searchID) return;
              if (data.results && data.results.length > 0) {
                const resourceIDs = data.results.map(result => result.id);
                this.loadData(resourceIDs, {})
                  .promise
                  .then(() => {
                    if (this.cancellable && this.cancellable.isCancelled()) return;
                    const resources = sortResourcesWithFallback(resourceIDs, this.props.graph, "label", this.props.viewLang, this.props.defaultLang);
                    this.setState({
                      resources,
                      updateAt: undefined,
                      noResults: false,
                      noneFound: false,
                      isLoading: false
                    });
                  })
                  .catch((e) => {
                    if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
                  });
              }
              else {
                if (this.cancellable && this.cancellable.isCancelled()) return;
                this.setState({
                  isLoading: false,
                  updateAt: undefined,
                  noneFound: true,
                  noResults: false
                });
              }
            })
            .catch((e) => {
              if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
            });
        });
      });
  };

  doSearch(payload, uuid) {
    this.cancellable = makeCancelable(GS.search(payload, uuid));
    return this.cancellable;
  }

  loadData(iris, options) {
    this.cancellable = makeCancelable(GS.loadData(iris, options, this.props.graph, true));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
    if (this.hcTimeout) clearTimeout(this.hcTimeout);
  }

  onAddNewNode = (resourceIRI, reservationID, label) => {
    this.props.onAddNewNode(resourceIRI, reservationID, label);
    this.setState({ updateAt: Date.now() });
    if (this.hcTimeout) clearTimeout(this.hcTimeout);
    // If there is a possibility the new resource should appear in the list refresh
    if (this.state.text === "" || label.toLowerCase().includes(this.state.text.toLowerCase())) {
      this.hcTimeout = setTimeout(() => {
        // But only do it if user has not searched in the meantime
        if (this.state.updateAt !== undefined) {
          this.handlePageChange(this.state.text === "");
        }
      }, 2000);
    }
  };

  onAddExistingNode = (resourceIRI) => {
    this.props.onAddExistingNode(resourceIRI);
    this.setState({ updateAt: Date.now() });
    if (this.hcTimeout) clearTimeout(this.hcTimeout);
    // If there is a possibility the new resource should appear in the list refresh
    this.hcTimeout = setTimeout(() => {
      // But only do it if user has not searched in the meantime
      if (this.state.updateAt !== undefined) {
        this.handlePageChange(this.state.text === "");
      }
    }, 2000);
  };

  render() {

    const { isLoading, resources, text, isErrored, noneFound } = this.state;
    const { activeResource, data, onSelect, defaultLang, viewLang, onSelectViewLanguage, projectUsers, addToDataset,
      defaultClasses, iriOptions, useSlashIRI, useUuidIRI, onFoundResource, ontologyProjects, useCounterIRI, counters,
      onDeleteResource, classes, datasetIRI, appBar, isReadOnly, graph, project, entitlements } = this.props;

    if (isErrored) return <Errored data={isErrored} />;

    return (
      <div>
        <div className={classes.controlPanel}>
          <div className={classes.views}>
            {appBar}
          </div>
          <div className={classes.controlPanelInner}>
            <div className={classes.controlPanelAdd}>
              {!isReadOnly && <CreateNodeDialogContainer
                componentIRI={data.id}
                useSlashIRI={useSlashIRI}
                useUuidIRI={useUuidIRI}
                useCounterIRI={useCounterIRI}
                counters={counters}
                useWorkflow={data.activateWorkflow === true}
                parentIRI={data.id}
                graph={graph}
                onlyComponentResources={true}
                iriOptions={iriOptions}
                onAddExistingNode={this.onAddExistingNode}
                onAddNewNode={this.onAddNewNode}
                searchClass="https://grafsync.graphifi.com/schema/gs/Resource"
                searchClassFilter={[
                  "https://grafsync.graphifi.com/schema/gs/Resource"
                ]}
                createDialog={CreateDataResourceDialog}
                title="Add Resource"
                placeholder="Enter resource label"
                name="Add a resource (Alt+n)"
              />}
            </div>
            <div className={classes.controlPanelSearch} style={
              { ...(!datasetIRI) && { marginLeft: 30 } }
            }>
              <PagingFilter
                autoFocus={true}
                lang={viewLang}
                handleFilterChange={this.handleFilterChange}
                text={text}
                maxLength={50}
                placeholder={"Max " + PAGE_SIZE + " displayed. Filter to refine."} />
            </div>
          </div>
          <div tabIndex={0} ref={this.controlRef} className={classes.tree}>
            {
              isLoading ?
                <Loading />
                :
                <React.Fragment>
                  <ResourceListStructure
                    resources={resources}
                    addToDataset={addToDataset}
                    datasetIRI={datasetIRI}
                    iriOptions={iriOptions}
                    graph={graph}
                    useWorkflow={data.activateWorkflow === true}
                    noneFound={noneFound}
                    activeResource={activeResource}
                    defaultLang={defaultLang}
                    viewLang={viewLang}
                    isReadOnly={isReadOnly}
                    onSelect={onSelect} />
                </React.Fragment>
            }
          </div>
        </div>
        <div className={classes.viewLanguagePanel}>
          <ProjectLanguageSelector selectedLang={viewLang} includeLabel={false} onSelect={onSelectViewLanguage} />
        </div>
        <div tabIndex={0} ref={this.infoRef} data-test="info-panel" className={classes.infoPanel}>
          {activeResource === null ?
            <div>
              <DatasetBatchOperations
                key={datasetIRI}
                entitlements={entitlements}
                datasetIRI={datasetIRI}
                useWorkflow={data.activateWorkflow === true}
                viewLang={viewLang}
                iriOptions={iriOptions}
                annotate={data.activateAnnotate === true}
                isReadOnly={isReadOnly}
                defaultClasses={data.defaultClass}
                graph={graph}
                useSlashIRI={useSlashIRI}
                useUuidIRI={useUuidIRI}
                project={project} />
              {datasetIRI &&
                <ComponentHint text="dataset" />
              }
              <ActionText offset="180px">Select a resource to view the detail.</ActionText>
            </div>
            :
            <ResourceData
              key={activeResource}
              handleNotAvailable={true}
              id={activeResource}
              entitlements={entitlements}
              projectUsers={projectUsers}
              datasetIRI={datasetIRI}
              viewLang={viewLang}
              ontologyProjects={ontologyProjects}
              onFoundResource={onFoundResource}
              propertyTemplates={data.associatedTemplate}
              defaultClasses={defaultClasses}
              graph={graph}
              useSlashIRI={useSlashIRI}
              useUuidIRI={useUuidIRI}
              linkedData={data.linkedData}
              associatedProject={data.associatedProject ?
                Array.isArray(data.associatedProject) ?
                  data.associatedProject
                  :
                  [data.associatedProject]
                :
                []
              }
              useWorkflow={data.activateWorkflow === true}
              annotate={data.activateAnnotate === true}
              project={project}
              resourceIRI={activeResource}
              onDeleteResource={onDeleteResource} />
          }
        </div>
      </div>
    );
  }

}

ResourceView.defaultProps = {
  data: {}
};

ResourceView.propTypes = {
  appBar: PropTypes.node.isRequired,
  entitlements: PropTypes.array.isRequired,
  graph: PropTypes.string.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  useSlashIRI: PropTypes.bool,
  useUuidIRI: PropTypes.bool,
  useCounterIRI: PropTypes.bool,
  counters: PropTypes.object,
  defaultClasses: PropTypes.array,
  datasetIRI: PropTypes.string,
  data: PropTypes.object.isRequired,
  project: PropTypes.string.isRequired,
  onAddExistingNode: PropTypes.func,
  onAddNewNode: PropTypes.func,
  onSelectViewLanguage: PropTypes.func.isRequired,
  addToDataset: PropTypes.func,
  projectUsers: PropTypes.array,
  activeResource: PropTypes.string,
  onFoundResource: PropTypes.func,
  ontologyProjects: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
  onDeleteResource: PropTypes.func.isRequired,
  defaultLang: PropTypes.string.isRequired,
  viewLang: PropTypes.string,
  deletedResources: PropTypes.arrayOf(PropTypes.string).isRequired,
  classes: PropTypes.object,
  iriOptions: PropTypes.object
};

export default withStyles(styles)(hotkeys(ResourceView));